import type { FC } from "react";
import { useCallback, useMemo } from "react";

import clsx from "clsx";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import HomeIcon from "assets/icons/outlined/home.svg?react";
import { getPaths } from "core/getPaths";

import { useMatch } from "shared/hooks";
import type { AdvancingTeamRuleModel, FullTeamModel, MatchModel } from "shared/types";
import { ImageSizesEnum, Img, SkeletonBase, Text } from "shared/uikit";
import { TextVariantsEnum } from "shared/uikit/types";
import { getTeamNameByAdvancedRule } from "utils/getMatchInfo";

import { PenaltyShootoutResult } from "../../types/TeamModel";

const { team: teamRoutes } = getPaths();

const Match: FC<{ loading: boolean; match?: MatchModel }> = ({ loading, match }) => {
	const { t } = useTranslation();

	const { getMatchData } = useMatch();
	const { matchInfo } = getMatchData();

	const getMatchDate = useCallback((date?: string | null) => {
		if (date) {
			const luxonDate = DateTime.fromISO(date);
			return luxonDate.isValid ? luxonDate.toFormat("ccc, d LLL") : date;
		}
		return "";
	}, []);

	const getMatchTime = useCallback((date?: string | null) => {
		if (date) {
			const luxonDate = DateTime.fromISO(date);
			return luxonDate.isValid ? luxonDate.toFormat("h:mm a") : date;
		}
		return "";
	}, []);

	const showScore = useMemo(
		() =>
			!!matchInfo?.result &&
			typeof matchInfo?.homeTeam?.score === "number" &&
			typeof matchInfo.awayTeam?.score === "number",
		[matchInfo]
	);

	const renderTeam = useCallback(
		({
			isHome,
			team,
			advancingRule,
			match,
			matchGroupName
		}: {
			isHome: boolean;
			team?: FullTeamModel | null;
			advancingRule?: AdvancingTeamRuleModel | null;
			match?: Partial<MatchModel> | null;
			matchGroupName?: string;
		}) => {
			if (loading) {
				return (
					<div
						className={clsx(
							"flex items-center flex-1 gap-4 justify-end min-w-0",
							!isHome && "flex-row-reverse",
							isHome ? "max-sm:order-2" : "max-sm:order-3",
							"max-sm:w-full max-sm:flex-none max-sm:flex-row-reverse max-sm:justify-end max-sm:gap-3"
						)}
					>
						<div className="min-w-0">
							<div className="flex items-center gap-2 max-sm:flex-row-reverse max-sm:justify-end">
								{isHome && <HomeIcon className="w-4 h-4 fill-gray-300" />}
								<SkeletonBase width={160} height={16} fullScale />
							</div>
							<div className="flex items-center gap-2 mt-1 justify-end">
								<SkeletonBase width={80} height={14} fullScale />
							</div>
						</div>
						<SkeletonBase width={40} height={40} variant="circular" fullScale />
					</div>
				);
			}

			if (team) {
				return (
					<Link
						to={`${teamRoutes.team.getPath()}/${team?.id}`}
						className={clsx(
							"flex items-center flex-1 gap-4 justify-end min-w-0 no-underline",
							!isHome && "flex-row-reverse",
							isHome ? "max-sm:order-2" : "max-sm:order-3",
							"max-sm:w-full max-sm:flex-none max-sm:flex-row-reverse max-sm:justify-end max-sm:gap-3"
						)}
					>
						<div className="min-w-0 text-right">
							<div className="flex items-center gap-2 max-sm:flex-row-reverse max-sm:justify-end">
								{isHome && <HomeIcon className="w-4 h-4 fill-gray-300 shrink-0" />}
								<Text variants={TextVariantsEnum.H7} className="truncate">
									{team?.name}
								</Text>
							</div>
							<div className="flex items-center gap-2 mt-1 justify-end">
								<Text variants={TextVariantsEnum.Caption3} className="text-hint truncate">
									{team?.club?.name}
								</Text>
							</div>
						</div>
						<Img src={team?.club?.logo} alt={team?.name} size={ImageSizesEnum.Small40} circle className="shrink-0" />
					</Link>
				);
			}

			if (advancingRule) {
				const teamName = getTeamNameByAdvancedRule({
					isHome,
					advancingRule,
					match,
					matchGroupName,
					t
				});

				return (
					<div
						className={clsx(
							"flex items-center gap-4 min-w-0",
							isHome && "flex-row-reverse",
							isHome ? "max-sm:order-2" : "max-sm:order-3",
							"max-sm:w-full max-sm:flex-row-reverse max-sm:justify-end max-sm:gap-3"
						)}
					>
						<div className="relative">
							<Img src={null} alt="?" size={ImageSizesEnum.Small64} circle />
						</div>
						<div className="flex flex-col gap-1 min-w-0 text-right">
							<div className="flex items-center gap-2 max-sm:flex-row-reverse max-sm:justify-end">
								{isHome && <HomeIcon className="w-4 h-4 svg-paths:fill-gray-300 shrink-0" />}
								<Text variants={TextVariantsEnum.Caption3} className="truncate">
									{teamName}
								</Text>
							</div>
						</div>
					</div>
				);
			}

			return null;
		},
		[loading, t]
	);

	const renderMobileTeam = useCallback(
		({
			isHome,
			team,
			advancingRule,
			match,
			matchGroupName
		}: {
			isHome: boolean;
			team?: FullTeamModel | null;
			advancingRule?: AdvancingTeamRuleModel | null;
			match?: Partial<MatchModel> | null;
			matchGroupName?: string;
		}) => {
			const teamName = team
				? team.name
				: advancingRule
					? getTeamNameByAdvancedRule({
							isHome,
							advancingRule,
							match,
							matchGroupName,
							t
						})
					: "-";
			const clubName = team?.club?.name || "";
			const score = showScore ? (isHome ? matchInfo?.homeTeam?.score : matchInfo?.awayTeam?.score) : undefined;
			const content = (
				<div className="flex items-center justify-between gap-3 py-3">
					<div className="flex min-w-0 items-center gap-3">
						<Img
							src={team?.club?.logo || null}
							alt={teamName}
							size={ImageSizesEnum.Small40}
							circle
							className="shrink-0"
						/>
						<div className="min-w-0">
							<div className="flex min-w-0 items-center gap-1.5">
								<Text variants={TextVariantsEnum.H7} className="truncate">
									{teamName}
								</Text>
								{isHome && <HomeIcon className="w-4 h-4 shrink-0 fill-gray-300" />}
							</div>
							<Text variants={TextVariantsEnum.Caption3} className="text-hint truncate">
								{clubName || "-"}
							</Text>
						</div>
					</div>
					<Text variants={TextVariantsEnum.H6} className="shrink-0">
						{typeof score === "number" ? score : "-"}
					</Text>
				</div>
			);

			if (team?.id) {
				return (
					<Link to={`${teamRoutes.team.getPath()}/${team.id}`} className="no-underline">
						{content}
					</Link>
				);
			}

			return content;
		},
		[matchInfo?.awayTeam?.score, matchInfo?.homeTeam?.score, showScore, t]
	);

	return (
		<div className="w-full">
			<div className="pt-4 max-sm:pt-3">
				<div className="flex align-center justify-center gap-7 max-sm:hidden">
					{renderTeam({
						team: match?.homeTeam,
						isHome: true,
						advancingRule: match?.homeTeamAdvancingRule,
						match,
						matchGroupName: match?.group?.name
					})}
					<div className="flex flex-col items-center justify-center shrink-0 max-sm:order-1 max-sm:w-full">
						{/* TODO: either render time of match or result of match when API is ready */}

						{loading ? (
							<>
								<SkeletonBase width={60} height={18} fullScale />
								<SkeletonBase width={90} height={14} fullScale className="mt-1" />
							</>
						) : (
							<>
								{showScore ? (
									<>
										<Text variants={TextVariantsEnum.H4}>
											{matchInfo!.homeTeam!.score} : {matchInfo!.awayTeam!.score}
										</Text>
										{match?.result && (
											<Text variants={TextVariantsEnum.BodySmall}>
												Pen{" "}
												{match?.homeTeam?.penaltyShootouts?.filter(x => x.result === PenaltyShootoutResult.goal)
													?.length || 0}{" "}
												-{" "}
												{match?.awayTeam?.penaltyShootouts?.filter(x => x.result === PenaltyShootoutResult.goal)
													?.length || 0}
											</Text>
										)}
									</>
								) : (
									<Text variants={TextVariantsEnum.H8}>{getMatchTime(match?.playedAt)}</Text>
								)}
								<Text variants={TextVariantsEnum.H7} className="text-hint">
									{getMatchDate(match?.playedAt || match?.date) || "Unscheduled"}
								</Text>
							</>
						)}
					</div>
					{renderTeam({
						team: match?.awayTeam,
						isHome: false,
						advancingRule: match?.awayTeamAdvancingRule,
						match,
						matchGroupName: match?.group?.name
					})}
				</div>
				<div className="hidden max-sm:flex max-sm:flex-col max-sm:gap-4">
					<div className="rounded-xl bg-gray-50 px-4 py-3 text-center">
						{loading ? (
							<div className="flex flex-col items-center">
								<SkeletonBase width={70} height={26} fullScale />
								<SkeletonBase width={90} height={16} fullScale className="mt-1" />
							</div>
						) : (
							<>
								{showScore ? (
									<>
										<Text variants={TextVariantsEnum.H4}>
											{matchInfo!.homeTeam!.score} : {matchInfo!.awayTeam!.score}
										</Text>
										{match?.result && (
											<Text variants={TextVariantsEnum.BodySmall} className="text-hint">
												Pen{" "}
												{match?.homeTeam?.penaltyShootouts?.filter(x => x.result === PenaltyShootoutResult.goal)
													?.length || 0}{" "}
												-{" "}
												{match?.awayTeam?.penaltyShootouts?.filter(x => x.result === PenaltyShootoutResult.goal)
													?.length || 0}
											</Text>
										)}
									</>
								) : (
									<Text variants={TextVariantsEnum.H7}>{getMatchTime(match?.playedAt)}</Text>
								)}
								<Text variants={TextVariantsEnum.H7} className="text-hint">
									{getMatchDate(match?.playedAt || match?.date) || "Unscheduled"}
								</Text>
							</>
						)}
					</div>
					<div className="rounded-xl border border-gray-100 px-3 py-1">
						{renderMobileTeam({
							team: match?.homeTeam,
							isHome: true,
							advancingRule: match?.homeTeamAdvancingRule,
							match,
							matchGroupName: match?.group?.name
						})}
						<div className="border-t border-gray-100" />
						{renderMobileTeam({
							team: match?.awayTeam,
							isHome: false,
							advancingRule: match?.awayTeamAdvancingRule,
							match,
							matchGroupName: match?.group?.name
						})}
					</div>
				</div>
			</div>
		</div>
	);
};

export default Match;
